<template>
  <FlexCol gap-1 class="relative" ref="triggerRef">
    <Label :label="label" :required="required" :error="error" v-if="label" />
    <InputArc :error="error" :disabled="disabled">
      <template #input>
        <div @click="toggle()" :class="{ 'cursor-pointer': !disabled }">
          <span v-if="text">{{ text }}</span>
          <span v-else class="text-gray-400 italic"> {{ placeholder ?? 'Selecione' }} </span>
        </div>
      </template>
      <template #pos-icon>
        <IconInput icon="fa-xmark" class="border-l" @click="clear()" v-if="selectedItem && !disabled" />
        <IconInput :icon="open ? 'fa-chevron-up' : 'fa-chevron-down'" class="border-l" @click="toggle()" />
      </template>
    </InputArc>
    <Error :message="error" v-if="error" />
    <Teleport to="body">
      <TheTransition>
        <div v-if="open && !disabled" :style="popupStyle" class="fixed z-[2000]" data-floating-root="true">
          <Card rounded border noSpace class="max-h-[168px] overflow-y-scroll text-sm border-slate-300">
            <Flex
              justify-start
              items-center
              v-for="(item, i) in items"
              :key="i"
              class="p-2 cursor-pointer transition-colors"
              :class="isSelected(item) ? 'bg-primary-500 text-white hover:bg-primary-600' : 'hover:bg-slate-100'"
              @click="select(item)">
              <span v-if="showId"> {{ item[itemValue] }} - </span>
              {{ item[itemText] }}
            </Flex>
          </Card>
        </div>
      </TheTransition>
    </Teleport>
  </FlexCol>
</template>

<script setup>
import { watch, ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: Array,
  modelValue: [String, Number],
  placeholder: String,
  items: Array,
  showId: Boolean,
  basic: Boolean,
  itemValue: { type: String, default: 'value' },
  itemText: { type: String, default: 'label' },
  up: Boolean,
})

const emits = defineEmits(['update:modelValue'])
const localValue = ref(props.modelValue)
const error = ref(props.error)
const open = ref(false)
const selectedItem = ref(null)
const triggerRef = ref(null)
const popupStyle = ref({})

const text = computed(() => {
  const item = props.items.find((item) => {
    if (!props.basic) {
      return item[props.itemValue] == localValue.value
    }
    return item
  })

  if (item) {
    selectedItem.value = item
    if (!props.basic) {
      return item[props.itemText]
    }
    return item
  }
  return
})

const toggle = () => {
  open.value = !open.value
  if (open.value) {
    nextTick(updatePopupPosition)
  }
}

const clear = () => {
  selectedItem.value = null
  localValue.value = null
  open.value = false
}

const select = (item) => {
  selectedItem.value = item
  if (!props.basic) {
    localValue.value = item[props.itemValue]
  } else {
    localValue.value = item
  }
  toggle()
}

const isSelected = (item) => {
  if (props.basic) {
    return item === localValue.value
  }
  return item?.[props.itemValue] == localValue.value
}

const updatePopupPosition = () => {
  const triggerElement = triggerRef.value?.$el ?? triggerRef.value
  if (!open.value || !triggerElement || typeof triggerElement.getBoundingClientRect !== 'function') return

  const rect = triggerElement.getBoundingClientRect()
  popupStyle.value = {
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    top: upPosition(rect),
  }
}

const upPosition = (rect) => {
  if (props.up) {
    return `${Math.max(8, rect.top - 176)}px`
  }
  return `${rect.bottom + 8}px`
}

const handleClickOutside = (event) => {
  const triggerElement = triggerRef.value?.$el ?? triggerRef.value
  if (!open.value || !triggerElement) return
  if (triggerElement.contains(event.target)) return
  open.value = false
}

watch(
  () => props.modelValue,
  (newValue) => {
    localValue.value = newValue
  }
)

watch(
  () => props.error,
  (newValue) => {
    error.value = newValue
  }
)

watch(localValue, (newValue) => {
  emits('update:modelValue', newValue)
})

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition)
    setTimeout(() => document.addEventListener('click', handleClickOutside), 0)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

onMounted(() => {
  window.addEventListener('resize', updatePopupPosition)
  window.addEventListener('scroll', updatePopupPosition, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePopupPosition)
  window.removeEventListener('scroll', updatePopupPosition, true)
})
</script>
